import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, TextField, CircularProgress, Autocomplete,
  FormControl, InputLabel, Select, MenuItem, Alert, Chip,
  Typography, Divider, Switch, FormControlLabel,
} from '@mui/material';
import { debounce } from 'lodash';
import venueWineRequestService, {
  WineNameLookupOption, RequestNewWinePayload,
} from '../../services/venueWineRequestService';
import type {
  ProducerOption, RegionOption, GrapeOption,
  WineColor, WineType, WineSubType,
} from '../../services/wineNameService';
import type { Wine } from '../../services/wineService';

interface Props {
  open: boolean;
  venueId: string;
  initialQuery: string;
  onCreated: (wine: Wine) => void;
  onClose: () => void;
}

const COLOR_OPTIONS: WineColor[] = ['RED', 'WHITE', 'ROSE', 'ORANGE', 'SPARKLING', 'DESSERT', 'FORTIFIED'];
const TYPE_OPTIONS: WineType[] = ['WINE', 'SPIRITS', 'BEER', 'OTHER'];
const SUB_TYPE_OPTIONS: WineSubType[] = ['STILL', 'SPARKLING', 'FORTIFIED', 'DESSERT', 'SWEET', 'NATURAL'];

type VintageMode = 'YEAR' | 'NV' | 'MV';

/**
 * RequestWineModal — VENUE_USER (and admins working in venue context) submit
 * provisional wine identities here. Backend forces `needsReview: true,
 * source: 'venue_request', createdBy: req.user.id`. Admins later approve,
 * edit, merge, or reject in the existing /admin/governance/review-queue.
 *
 * Crucially: pickers are search-only. There is no inline create-producer /
 * create-region / create-grape — those would be cross-venue master-data
 * mutations, out of scope for venue users. The admin CreateWineModal still
 * has those affordances; this is the smaller, safer cousin.
 */
const RequestWineModal: React.FC<Props> = ({ open, venueId, initialQuery, onCreated, onClose }) => {
  // ── Existing wine-name selection (optional shortcut) ──────────────────────
  const [useExistingWineName, setUseExistingWineName] = useState(false);
  const [wineNameOption, setWineNameOption] = useState<WineNameLookupOption | null>(null);
  const [wineNameOptions, setWineNameOptions] = useState<WineNameLookupOption[]>([]);
  const [wineNameQuery, setWineNameQuery] = useState('');
  const [wineNameLoading, setWineNameLoading] = useState(false);

  // ── New wine-name fields (used when not picking an existing one) ──────────
  const [producer, setProducer] = useState<ProducerOption | null>(null);
  const [producerQuery, setProducerQuery] = useState('');
  const [producerOptions, setProducerOptions] = useState<ProducerOption[]>([]);
  const [producerLoading, setProducerLoading] = useState(false);

  const [region, setRegion] = useState<RegionOption | null>(null);
  const [regionQuery, setRegionQuery] = useState('');
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [regionLoading, setRegionLoading] = useState(false);

  const [grapes, setGrapes] = useState<GrapeOption[]>([]);
  const [grapeQuery, setGrapeQuery] = useState('');
  const [grapeOptions, setGrapeOptions] = useState<GrapeOption[]>([]);
  const [grapeLoading, setGrapeLoading] = useState(false);

  const [specialName, setSpecialName] = useState('');
  const [color, setColor] = useState<WineColor | ''>('');
  const [wineType, setWineType] = useState<WineType>('WINE');
  const [subType, setSubType] = useState<WineSubType | ''>('');

  // ── Vintage ────────────────────────────────────────────────────────────────
  const [vintageMode, setVintageMode] = useState<VintageMode>('YEAR');
  const [vintageYear, setVintageYear] = useState<string>('');

  // ── Inline-request sub-dialogs (Producer / Region / Grape) ───────────────
  // Each gets its own open flag + mini form state. Triggered by the "Don't see
  // it? Request a new …" link under the corresponding picker.
  const [producerRequestOpen, setProducerRequestOpen] = useState(false);
  const [producerRequestName, setProducerRequestName] = useState('');
  const [producerRequestCountry, setProducerRequestCountry] = useState('');
  const [producerRequestSaving, setProducerRequestSaving] = useState(false);
  const [producerRequestError, setProducerRequestError] = useState<string | null>(null);

  const [regionRequestOpen, setRegionRequestOpen] = useState(false);
  const [regionRequestName, setRegionRequestName] = useState('');
  const [regionRequestSaving, setRegionRequestSaving] = useState(false);
  const [regionRequestError, setRegionRequestError] = useState<string | null>(null);

  const [grapeRequestOpen, setGrapeRequestOpen] = useState(false);
  const [grapeRequestName, setGrapeRequestName] = useState('');
  const [grapeRequestColor, setGrapeRequestColor] = useState<'RED' | 'WHITE' | 'PINK_ROSE'>('RED');
  const [grapeRequestSaving, setGrapeRequestSaving] = useState(false);
  const [grapeRequestError, setGrapeRequestError] = useState<string | null>(null);

  // ── Submission state ──────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Reset on open ─────────────────────────────────────────────────────────
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setUseExistingWineName(false);
      setWineNameOption(null);
      setWineNameOptions([]);
      setWineNameQuery(initialQuery ?? '');
      setProducer(null);
      setProducerQuery('');
      setProducerOptions([]);
      setRegion(null);
      setRegionQuery('');
      setRegionOptions([]);
      setGrapes([]);
      setGrapeQuery('');
      setGrapeOptions([]);
      setSpecialName('');
      setColor('');
      setWineType('WINE');
      setSubType('');
      setVintageMode('YEAR');
      setVintageYear('');
      setSubmitError(null);
    }
    prevOpen.current = open;
  }, [open, initialQuery]);

  // ── Picker searches (debounced) ───────────────────────────────────────────
  const debouncedSearchProducers = useMemo(
    () => debounce(async (q: string) => {
      if (!venueId) return;
      setProducerLoading(true);
      try {
        const result = await venueWineRequestService.searchProducers(venueId, q);
        setProducerOptions(result);
      } finally {
        setProducerLoading(false);
      }
    }, 250),
    [venueId],
  );

  const debouncedSearchRegions = useMemo(
    () => debounce(async (q: string) => {
      if (!venueId) return;
      setRegionLoading(true);
      try {
        const result = await venueWineRequestService.searchRegions(venueId, q);
        setRegionOptions(result);
      } finally {
        setRegionLoading(false);
      }
    }, 250),
    [venueId],
  );

  const debouncedSearchGrapes = useMemo(
    () => debounce(async (q: string) => {
      if (!venueId) return;
      setGrapeLoading(true);
      try {
        const result = await venueWineRequestService.searchGrapes(venueId, q);
        setGrapeOptions(result);
      } finally {
        setGrapeLoading(false);
      }
    }, 250),
    [venueId],
  );

  const debouncedSearchWineNames = useMemo(
    () => debounce(async (q: string) => {
      if (!venueId) return;
      setWineNameLoading(true);
      try {
        const result = await venueWineRequestService.searchWineNames(venueId, q);
        setWineNameOptions(result);
      } finally {
        setWineNameLoading(false);
      }
    }, 250),
    [venueId],
  );

  // Cancel pending searches on close / unmount.
  useEffect(() => () => {
    debouncedSearchProducers.cancel();
    debouncedSearchRegions.cancel();
    debouncedSearchGrapes.cancel();
    debouncedSearchWineNames.cancel();
  }, [debouncedSearchProducers, debouncedSearchRegions, debouncedSearchGrapes, debouncedSearchWineNames]);

  // ── Inline-request handlers ──────────────────────────────────────────────
  // The shared pattern: open the sub-dialog with the typed query pre-filled,
  // submit, set the result as the picker value, close. On 409 the API surfaces
  // `existing` — we select it instead so the user moves forward either way.

  const openProducerRequest = () => {
    setProducerRequestName(producerQuery.trim());
    setProducerRequestCountry('');
    setProducerRequestError(null);
    setProducerRequestOpen(true);
  };

  const submitProducerRequest = async () => {
    const name = producerRequestName.trim();
    if (!name) { setProducerRequestError('Name is required.'); return; }
    setProducerRequestError(null);
    setProducerRequestSaving(true);
    try {
      const created = await venueWineRequestService.requestProducer(venueId, {
        name,
        ...(producerRequestCountry.trim() && { country: producerRequestCountry.trim() }),
      });
      setProducer(created);
      setProducerOptions((prev) => [created, ...prev.filter(p => p._id !== created._id)]);
      setProducerRequestOpen(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const existing = err?.response?.data?.existing;
      if (code === 'PRODUCER_DUPLICATE' && existing?._id) {
        const opt: ProducerOption = { _id: existing._id, name: existing.name, fullName: existing.fullName };
        setProducer(opt);
        setProducerOptions((prev) => [opt, ...prev.filter(p => p._id !== opt._id)]);
        setProducerRequestOpen(false);
        return;
      }
      setProducerRequestError(err?.response?.data?.message ?? err?.message ?? 'Failed to submit producer.');
    } finally {
      setProducerRequestSaving(false);
    }
  };

  const openRegionRequest = () => {
    setRegionRequestName(regionQuery.trim());
    setRegionRequestError(null);
    setRegionRequestOpen(true);
  };

  const submitRegionRequest = async () => {
    const name = regionRequestName.trim();
    if (!name) { setRegionRequestError('Name is required.'); return; }
    setRegionRequestError(null);
    setRegionRequestSaving(true);
    try {
      const created = await venueWineRequestService.requestRegion(venueId, { name });
      setRegion(created);
      setRegionOptions((prev) => [created, ...prev.filter(r => r._id !== created._id)]);
      setRegionRequestOpen(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const existing = err?.response?.data?.existing;
      if (code === 'REGION_DUPLICATE' && existing?._id) {
        const opt: RegionOption = { _id: existing._id, name: existing.name };
        setRegion(opt);
        setRegionOptions((prev) => [opt, ...prev.filter(r => r._id !== opt._id)]);
        setRegionRequestOpen(false);
        return;
      }
      setRegionRequestError(err?.response?.data?.message ?? err?.message ?? 'Failed to submit region.');
    } finally {
      setRegionRequestSaving(false);
    }
  };

  const openGrapeRequest = () => {
    setGrapeRequestName(grapeQuery.trim());
    // Default the new grape's color to the wine's color when sensible.
    setGrapeRequestColor(color === 'WHITE' ? 'WHITE' : color === 'ROSE' ? 'PINK_ROSE' : 'RED');
    setGrapeRequestError(null);
    setGrapeRequestOpen(true);
  };

  const submitGrapeRequest = async () => {
    const primaryName = grapeRequestName.trim();
    if (!primaryName) { setGrapeRequestError('Primary name is required.'); return; }
    setGrapeRequestError(null);
    setGrapeRequestSaving(true);
    try {
      const created = await venueWineRequestService.requestGrape(venueId, {
        primaryName,
        color: grapeRequestColor,
      });
      setGrapes((prev) => [...prev, created].slice(0, 3));
      setGrapeOptions((prev) => [created, ...prev.filter(g => g._id !== created._id)]);
      setGrapeRequestOpen(false);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const existing = err?.response?.data?.existing;
      if (code === 'GRAPE_DUPLICATE' && existing?._id) {
        const opt: GrapeOption = { _id: existing._id, primaryName: existing.primaryName, color: existing.color };
        setGrapes((prev) => [...prev.filter(g => g._id !== opt._id), opt].slice(0, 3));
        setGrapeOptions((prev) => [opt, ...prev.filter(g => g._id !== opt._id)]);
        setGrapeRequestOpen(false);
        return;
      }
      setGrapeRequestError(err?.response?.data?.message ?? err?.message ?? 'Failed to submit grape.');
    } finally {
      setGrapeRequestSaving(false);
    }
  };

  // Show the "Don't see it? Request new …" link as a stable affordance under
  // each picker whenever no entity is selected (and we're not in
  // "use existing wine name" mode). We deliberately do NOT gate on input
  // length or exact-match here: MUI Autocomplete's default
  // `clearOnBlur: true` wipes the inputValue the moment the user mouses
  // outside the field, which would hide the link as soon as they glance at
  // the "No options" dropdown. Dropping the query-length gate makes the
  // affordance reliable; the sub-dialog still pre-fills with the current
  // query if there is one, and the user can type the name there if not.
  const showProducerRequestLink = !useExistingWineName && !producer;
  const showRegionRequestLink = !useExistingWineName && !region;
  const showGrapeRequestLink = !useExistingWineName && grapes.length < 3;

  // ── Submit ────────────────────────────────────────────────────────────────
  const buildPayload = (): RequestNewWinePayload | null => {
    const vintageType: RequestNewWinePayload['vintageType'] =
      vintageMode === 'NV' ? 'non_vintage' : vintageMode === 'MV' ? 'multi_vintage' : 'single_vintage';

    const payload: RequestNewWinePayload = { vintageType };

    if (vintageType === 'single_vintage') {
      const year = parseInt(vintageYear, 10);
      if (!Number.isFinite(year) || year < 1800 || year > 2100) {
        setSubmitError('Enter a valid vintage year.');
        return null;
      }
      payload.vintage = year;
    }

    if (useExistingWineName) {
      if (!wineNameOption) {
        setSubmitError('Pick an existing wine name or switch off "Use existing wine name".');
        return null;
      }
      payload.wineNameId = wineNameOption._id;
    } else {
      if (!producer) { setSubmitError('Producer is required.'); return null; }
      if (!region) { setSubmitError('Region is required.'); return null; }
      if (!color) { setSubmitError('Color is required.'); return null; }
      payload.producerId = producer._id;
      payload.regionId = region._id;
      payload.color = color;
      payload.type = wineType;
      if (subType) payload.subType = subType;
      if (specialName.trim()) payload.specialName = specialName.trim();
      if (grapes.length > 0) payload.labelGrapeIds = grapes.map(g => g._id);
    }

    return payload;
  };

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      const wine = await venueWineRequestService.requestNew(venueId, payload);
      onCreated(wine);
      onClose();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const message =
        code === 'WINE_DUPLICATE'
          ? 'A wine matching this combination already exists. Search for it instead.'
          : err?.response?.data?.message ?? err?.message ?? 'Failed to submit request.';
      setSubmitError(message);
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, useExistingWineName, wineNameOption, producer, region, grapes, specialName, color, wineType, subType, vintageMode, vintageYear, onCreated, onClose]);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request a new wine</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Alert severity="info">
          The wine will be flagged <strong>Needs Review</strong>. An admin will approve, edit, or merge it.
          You can still link it to your inventory or wine list right away.
        </Alert>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <FormControlLabel
          control={
            <Switch
              checked={useExistingWineName}
              onChange={(_e, checked) => setUseExistingWineName(checked)}
            />
          }
          label="Use existing wine name (request a new vintage only)"
        />

        {useExistingWineName ? (
          <Autocomplete<WineNameLookupOption>
            value={wineNameOption}
            inputValue={wineNameQuery}
            options={wineNameOptions}
            loading={wineNameLoading}
            getOptionLabel={(o) => o?.displayName ?? ''}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            filterOptions={x => x}
            onInputChange={(_e, v) => { setWineNameQuery(v); debouncedSearchWineNames(v); }}
            onChange={(_e, v) => setWineNameOption(v)}
            renderInput={params => (
              <TextField
                {...params}
                label="Wine name"
                required
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {wineNameLoading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        ) : (
          <>
            <Box>
              <Autocomplete<ProducerOption>
                value={producer}
                inputValue={producerQuery}
                options={producerOptions}
                loading={producerLoading}
                getOptionLabel={(o) => o?.fullName || o?.name || ''}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                filterOptions={x => x}
                onInputChange={(_e, v) => { setProducerQuery(v); debouncedSearchProducers(v); }}
                onChange={(_e, v) => setProducer(v)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Producer"
                    required
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {producerLoading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
              {showProducerRequestLink && (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Don't see it?</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={openProducerRequest}
                    sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}
                  >
                    Request new producer…
                  </Button>
                </Box>
              )}
            </Box>

            <Box>
              <Autocomplete<RegionOption>
                value={region}
                inputValue={regionQuery}
                options={regionOptions}
                loading={regionLoading}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                filterOptions={x => x}
                onInputChange={(_e, v) => { setRegionQuery(v); debouncedSearchRegions(v); }}
                onChange={(_e, v) => setRegion(v)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Region"
                    required
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {regionLoading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
              {showRegionRequestLink && (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Don't see it?</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={openRegionRequest}
                    sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}
                  >
                    Request new region…
                  </Button>
                </Box>
              )}
            </Box>

            <Box>
              <Autocomplete<GrapeOption, true>
                multiple
                value={grapes}
                inputValue={grapeQuery}
                options={grapeOptions}
                loading={grapeLoading}
                getOptionLabel={(o) => o?.primaryName || ''}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                filterOptions={x => x}
                onInputChange={(_e, v) => { setGrapeQuery(v); debouncedSearchGrapes(v); }}
                onChange={(_e, v) => setGrapes((v as GrapeOption[]).slice(0, 3))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option._id} label={option.primaryName} size="small" />
                  ))
                }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Label grapes (up to 3, optional)"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {grapeLoading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
              />
              {/* Grape request link — visible whenever fewer than 3 grapes
                  are selected (and we're not in 'use existing wine name' mode). */}
              {showGrapeRequestLink && (
                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">Don't see it?</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={openGrapeRequest}
                    sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}
                  >
                    Request new grape…
                  </Button>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl required fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  value={color}
                  label="Color"
                  onChange={(e) => setColor(e.target.value as WineColor)}
                >
                  {COLOR_OPTIONS.map(c => (
                    <MenuItem key={c} value={c}>{c.replace('_', ' ')}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={wineType}
                  label="Type"
                  onChange={(e) => setWineType(e.target.value as WineType)}
                >
                  {TYPE_OPTIONS.map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Sub-type</InputLabel>
                <Select
                  value={subType}
                  label="Sub-type"
                  onChange={(e) => setSubType(e.target.value as WineSubType)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {SUB_TYPE_OPTIONS.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Special name (optional)"
              value={specialName}
              onChange={(e) => setSpecialName(e.target.value)}
              placeholder="e.g. Reserva, Cuvée, Single Vineyard"
            />
          </>
        )}

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Vintage</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mode</InputLabel>
              <Select
                value={vintageMode}
                label="Mode"
                onChange={(e) => setVintageMode(e.target.value as VintageMode)}
              >
                <MenuItem value="YEAR">Year</MenuItem>
                <MenuItem value="NV">Non-vintage</MenuItem>
                <MenuItem value="MV">Multi-vintage</MenuItem>
              </Select>
            </FormControl>
            {vintageMode === 'YEAR' && (
              <TextField
                label="Year"
                size="small"
                type="number"
                value={vintageYear}
                onChange={(e) => setVintageYear(e.target.value)}
                required
                sx={{ width: 140 }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Submitting…' : 'Submit request'}
        </Button>
      </DialogActions>

      {/* ── Producer request sub-dialog ────────────────────────────────────── */}
      <Dialog open={producerRequestOpen} onClose={producerRequestSaving ? undefined : () => setProducerRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a new producer</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            The producer will be flagged <strong>Needs Review</strong>. An admin will approve, edit, or reject it later.
          </Alert>
          {producerRequestError && <Alert severity="error">{producerRequestError}</Alert>}
          <TextField
            label="Name"
            value={producerRequestName}
            onChange={(e) => setProducerRequestName(e.target.value)}
            required
            autoFocus
          />
          <TextField
            label="Country (optional)"
            value={producerRequestCountry}
            onChange={(e) => setProducerRequestCountry(e.target.value)}
            placeholder="Leave empty if unsure — admin will fill it in"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProducerRequestOpen(false)} disabled={producerRequestSaving}>Cancel</Button>
          <Button
            onClick={submitProducerRequest}
            variant="contained"
            disabled={producerRequestSaving || !producerRequestName.trim()}
            startIcon={producerRequestSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {producerRequestSaving ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Region request sub-dialog ──────────────────────────────────────── */}
      <Dialog open={regionRequestOpen} onClose={regionRequestSaving ? undefined : () => setRegionRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a new region</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            The region will be flagged <strong>Needs Review</strong>. An admin will approve, re-parent, or reject it later.
          </Alert>
          {regionRequestError && <Alert severity="error">{regionRequestError}</Alert>}
          <TextField
            label="Name"
            value={regionRequestName}
            onChange={(e) => setRegionRequestName(e.target.value)}
            required
            autoFocus
          />
          <Typography variant="caption" color="text.secondary">
            Created at the top level. An admin can place it under a parent region during review.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegionRequestOpen(false)} disabled={regionRequestSaving}>Cancel</Button>
          <Button
            onClick={submitRegionRequest}
            variant="contained"
            disabled={regionRequestSaving || !regionRequestName.trim()}
            startIcon={regionRequestSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {regionRequestSaving ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Grape request sub-dialog ───────────────────────────────────────── */}
      <Dialog open={grapeRequestOpen} onClose={grapeRequestSaving ? undefined : () => setGrapeRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a new grape</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            The grape will be flagged <strong>Needs Review</strong>. An admin will approve, edit aliases, or reject it later.
          </Alert>
          {grapeRequestError && <Alert severity="error">{grapeRequestError}</Alert>}
          <TextField
            label="Primary name"
            value={grapeRequestName}
            onChange={(e) => setGrapeRequestName(e.target.value)}
            required
            autoFocus
          />
          <FormControl required>
            <InputLabel>Color</InputLabel>
            <Select
              value={grapeRequestColor}
              label="Color"
              onChange={(e) => setGrapeRequestColor(e.target.value as 'RED' | 'WHITE' | 'PINK_ROSE')}
            >
              <MenuItem value="RED">Red</MenuItem>
              <MenuItem value="WHITE">White</MenuItem>
              <MenuItem value="PINK_ROSE">Pink / Rosé</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrapeRequestOpen(false)} disabled={grapeRequestSaving}>Cancel</Button>
          <Button
            onClick={submitGrapeRequest}
            variant="contained"
            disabled={grapeRequestSaving || !grapeRequestName.trim()}
            startIcon={grapeRequestSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {grapeRequestSaving ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RequestWineModal;
